/**
 * CSV exports (spec § 8.3).
 *
 * Two files: one row per PRODUCT for suppliers, and one row per shortlisted
 * supplier for the final review.
 *
 * Two details that matter for anyone opening these in Excel:
 *   - UTF-8 BOM. Without it Excel on Windows reads the file as the system
 *     codepage and every Chinese supplier name becomes mojibake.
 *   - RFC-4180 quoting: wrap when the value contains a comma, quote, newline
 *     or leading/trailing space, and escape an embedded quote by doubling it.
 */

import { REVIEW_KEYS, REVIEW_LABELS } from '../data/reviewSchema';
import { landed, money, num, primaryContact, riskFlags, toUSD } from '../rules';

const BOM = '﻿';

/** RFC-4180 field. */
function field(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s) || /^\s|\s$/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** CRLF line endings, as RFC-4180 specifies. */
const toCsv = (rows) => BOM + rows.map((r) => r.map(field).join(',')).join('\r\n');

/* ── suppliers-<date>.csv — one row per product ─────────────────────────── */

const SUPPLIER_COLUMNS = [
  'Fair',
  'Supplier',
  'Phase',
  'Hall',
  'Booth',
  'Type',
  'Contact',
  'Phone',
  'WeChat',
  'WhatsApp',
  'Email',
  'Website',
  'Product',
  'Category',
  'Price',
  'Currency',
  'Per',
  'Qty',
  'MOQ',
  'Best price',
  'Valid until',
  'FOB port',
  'Payment',
  'Production time',
  'Sample',
  'Carton',
  'Carton weight',
  'Units/carton',
  'USA cert',
  'Cert types',
  'Rating',
  'Decision',
  'Next step',
  'Revisit',
  'Revisit reason',
  'Appointment',
  'Sell at',
  'Sample paid',
  'Deposit paid',
  'Handover',
  'Notes',
  'Shortlist',
  'Final decision',
  'Landed/unit',
];

export function suppliersCsv({ suppliers, fairs }) {
  const rows = [SUPPLIER_COLUMNS];

  for (const s of suppliers || []) {
    const fair = (fairs || []).find((f) => f.id === s.fairId) || null;
    const contact = primaryContact(s) || {};
    const l = landed(s);
    const products = (s.products || []).length ? s.products : [null];

    for (const prod of products) {
      rows.push([
        fair ? fair.name : '',
        s.company,
        s.phase,
        s.hall,
        s.booth,
        s.type,
        contact.name,
        contact.phone,
        contact.wechat,
        contact.whatsapp,
        contact.email,
        contact.website,
        prod ? prod.name : '',
        prod ? prod.category : '',
        prod ? prod.price : '',
        prod ? prod.currency : '',
        prod ? prod.priceUnit : '',
        prod ? prod.qty : '',
        prod ? prod.moq : '',
        prod ? prod.bestPrice : '',
        prod ? prod.validUntil : '',
        prod ? prod.fobPort : '',
        prod ? prod.paymentTerms : '',
        prod ? prod.productionTime : '',
        prod ? prod.sample : '',
        prod && (prod.cartonL || prod.cartonW || prod.cartonH)
          ? `${prod.cartonL || ''}x${prod.cartonW || ''}x${prod.cartonH || ''} ${prod.lenUnit || ''}`.trim()
          : '',
        prod && prod.cartonWeight ? `${prod.cartonWeight} ${prod.wtUnit || ''}`.trim() : '',
        prod ? prod.unitsPerCarton : '',
        prod ? prod.cert : '',
        prod ? (prod.certTypes || []).join(' / ') : '',
        prod ? prod.rating : '',
        prod ? prod.decision : '',
        prod && prod.nextStep !== 'None' ? prod.nextStep : '',
        prod && prod.revisit ? 'Yes' : '',
        prod ? prod.revisitReason : '',
        prod ? prod.revisitAt : '',
        prod ? prod.sellPrice : '',
        prod ? prod.samplePaid : '',
        prod ? prod.depositPaid : '',
        prod ? prod.sampleHandover : '',
        prod ? prod.notes : '',
        s.inShortlist ? 'Yes' : '',
        s.decision || '',
        l.perUnit != null ? l.perUnit.toFixed(4) : '',
      ]);
    }
  }
  return toCsv(rows);
}

/* ── final-review-<date>.csv — shortlist with every review field ────────── */

export function finalReviewCsv({ suppliers, fairs }) {
  const finalists = (suppliers || []).filter((s) => s.inShortlist);

  const header = [
    'Fair',
    'Supplier',
    'Booth',
    'Product',
    'Quoted price',
    'Currency',
    'Quoted in USD',
    'Total landed',
    'Landed/unit',
    'Risk flag count',
    'Final decision',
    'Reason',
    'Next action',
    ...REVIEW_KEYS.map((k) => REVIEW_LABELS[k] || k),
  ];

  const rows = [header];

  for (const s of finalists) {
    const fair = (fairs || []).find((f) => f.id === s.fairId) || null;
    const prod = (s.products || []).find((x) => x.decision === 'Strong') || (s.products || [])[0] || {};
    const l = landed(s);
    const review = s.review || {};
    const quoted = num(prod.bestPrice || prod.price);
    const usd = prod.currency === 'USD' ? quoted : toUSD(quoted, prod.currency, fair);

    rows.push([
      fair ? fair.name : '',
      s.company,
      s.booth,
      prod.name || '',
      prod.price || '',
      prod.currency || '',
      usd != null ? usd.toFixed(4) : '',
      l.total ? l.total.toFixed(2) : '',
      l.perUnit != null ? l.perUnit.toFixed(4) : '',
      String(riskFlags(s).length),
      s.decision || '',
      review.final_reason || '',
      review.final_next || '',
      ...REVIEW_KEYS.map((k) => (review[k] == null ? '' : review[k])),
    ]);
  }
  return toCsv(rows);
}

/* ── Download ───────────────────────────────────────────────────────────── */

export function downloadCsv(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const csvStamp = () => new Date().toISOString().slice(0, 10);

export { money };
